import { PageSizing } from 'components/Layout/PageContentWidth';
import { ContentCard, PageWrapper } from '../styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPaymentMethodsQuery, getSummaryUsageQuery } from 'query/billing';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import Loader from '__components/Kizen/Loader';
import { PaymentMethodsBlock } from './blocks/PaymentMethods';
import { OverageBudgetsBlock } from './blocks/OverageBudgets';
import { PaymentContact } from './blocks/PaymentContact';
import { BusinessDeactivation } from './blocks/BusinessDeactivation';
import {
  getAuthTeamMember,
  getChosenBusiness,
} from 'store/authentication/selectors';

export const BillingDetailsPage = () => {
  const qs = new URLSearchParams(window.location.search);
  const isSuccess = qs.get('success') === 'true';
  const makePrimary = qs.get('makePrimary') === 'true';
  const previousExistingIds = qs.get('existing')?.split(',') || [];
  const [resetKey, setResetKey] = useState(() => Date.now());
  const queryClient = useQueryClient();
  const { billing: billingSettingsAccess } =
    useSelector(
      (s: any) => s.authentication.access.sections.settings_section
    ) ?? {};

  const chosenBusiness = useSelector(getChosenBusiness);
  const teamMember = useSelector(getAuthTeamMember);

  const teamMemberIsAdmin = teamMember.roles.includes(
    chosenBusiness.admin_permission_role?.id
  );

  const {
    data: paymentMethodsData,
    isLoading: paymentMethodsLoading,
    refetch: refetchPaymentMethods,
  } = useQuery(
    getPaymentMethodsQuery({
      throwOnError: false,
      // This query will fail if the user hasn't set up any payment methods, because
      // there is no Stripe customer created yet. It gets created automatically so this
      // is fine, but should be improved. (keegandonley)
      retry: (failureCount, error: any) => {
        if (error?.status === 400) {
          return false;
        }

        return failureCount < 3;
      },
    })
  );

  const {
    data: summaryData,
    isLoading: summaryUsageLoading,
    isRefetching: summaryUsageRefetching,
  } = useQuery(getSummaryUsageQuery());

  const defaultPaymentMethod = paymentMethodsData?.default_payment_method_id;

  const paymentMethods = useMemo(() => {
    return paymentMethodsData?.payment_methods || [];
  }, [paymentMethodsData]);

  const refetchOverageBlock = useCallback(async () => {
    // Use the query client to refetch because we need this data to be fresh on other pages
    await queryClient.refetchQueries({
      queryKey: getSummaryUsageQuery().queryKey,
    });

    setResetKey(Date.now());
  }, [queryClient]);

  if (paymentMethodsLoading || summaryUsageLoading) {
    return <Loader loading />;
  }

  return (
    <>
      <PageSizing>
        <div className="pb-20">
          <PageWrapper>
            <div className="flex flex-col gap-20">
              <div className="grid grid-cols-2 gap-20">
                <ContentCard>
                  <PaymentMethodsBlock
                    paymentMethods={paymentMethods}
                    refetchPaymentMethods={refetchPaymentMethods}
                    isSuccess={isSuccess}
                    makePrimary={makePrimary}
                    isLoading={paymentMethodsLoading}
                    defaultPaymentMethod={defaultPaymentMethod}
                    previousExistingIds={previousExistingIds}
                  />
                </ContentCard>
                <ContentCard>
                  <PaymentContact />
                </ContentCard>
              </div>
              <ContentCard>
                <OverageBudgetsBlock
                  key={resetKey}
                  usageSummary={summaryData}
                  refetch={refetchOverageBlock}
                  disabled={summaryUsageRefetching}
                  readOnly={!billingSettingsAccess.edit}
                />
              </ContentCard>
            </div>
            {teamMemberIsAdmin ? <BusinessDeactivation /> : null}
          </PageWrapper>
        </div>
      </PageSizing>
    </>
  );
};
